import { get } from "@/api/API";
import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { computed, reactive, ref } from "vue";

export type LicensePlan = {
  id: string;
  sublabel: string;
  description: string;
  canAddSeats: boolean;
  canAddWorkspaces: boolean;
  canBeOrderedDirectly: boolean;
  orderable: boolean;

  minSeats: number;

  subscriptionType: "organization" | "license";

  paymentPeriod: "month" | "year";
  price: number;

  priceOverride?: string;
  buttonOverride?: string;
  buttonOnclickOverride?: string;

  label: string;
  upgradeable: boolean;
};

const plans = reactive({} as { [id: string]: LicensePlan });
const upgradePlans = reactive({} as { [id: string]: {
  id: string;
  price: number;
} });
const plansPromise = ref<Promise<{ [id: string]: LicensePlan }>>();

export const subscriptionUpgrades = {
  "unlimitedWorkspaces": {
    id: "unlimited_workspaces",
  },
  "unlimitedStorage": {
    id: "unlimited_storage"
  },
  "publishing": {
    id: "publishing"
  },
  "support": {
    id: "support"
  }
};

export async function loadPlans() {
  plansPromise.value = get<LicensePlan[]>("/v1/plans", {
    sendAuthToken: false
  })
    .then((plansArray) => {
      plans.free = {
        id: "free",
        sublabel: "Free",
        description: "Free",
        canAddSeats: false,
        canAddWorkspaces: false,
        canBeOrderedDirectly: true,
        orderable: false,
        minSeats: 1,
        subscriptionType: "license",
        label: "Free",
        upgradeable: false,
        paymentPeriod: "month",
        price: 0
      };
      for (const plan of plansArray) {
        plans[plan.id] = plan;
      }
      return plans;
    })
    .finally(() => (plansPromise.value = undefined));
  return await plansPromise.value;
}

export async function loadUpgradePlans() {
  return get<{ [id: string]: {
    id: string;
    price: number;
  } }>("/v1/plans/upgrades", {
    sendAuthToken: false
  })
    .then((plansArray) => {
      for (const plan of Object.values(plansArray)) {
        upgradePlans[plan.id] = plan;
      }
    });
}

export function usePlans() {
  // load the plans if they do not exist
  if (Object.values(plans).length == 0 && plansPromise.value == null) {
    loadPlans();
  }

  if (Object.values(upgradePlans).length == 0) {
    loadUpgradePlans();
  }

  const loading = computed(() => plansPromise.value !== null);

  const orderablePlans = computed(() => {
    return pickBy(plans, (plan) => plan.orderable);
  });

  return {
    plans,
    upgradePlans,
    orderablePlans,
    plansPromise,
    loading,
    loadPlans
  };
}
